import type { VoidFunctionComponent } from 'react';

import { SNIPPET_DETECTION } from '../../../../shared/featureFlags/flagNames';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { LabeledToggle } from '../../generic/Forms/LabeledToggle';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import PaidPill from '../../generic/Pill/PaidPill';

import Section from './SettingsPageSection';

import css from './Settings.module.less';

type SnippetScanningSettingsSectionProps = {
  /** Current state of snippet scanning */
  isSnippetScanningEnabled: boolean;
  /** Callback when checkbox is toggled */
  onToggleSnippetScanning: (isChecked: boolean) => void;
  /** Whether main scanning is enabled */
  isScanningEnabled: boolean;
  /** User permission to edit policy */
  canEditPolicy: boolean;
};

/**
 * SnippetScanningSettingsSection
 *
 * Renders a toggle control for enabling/disabling snippet scanning
 * with context-appropriate messaging based on scan type.
 * When the SNIPPET_DETECTION feature flag is not enabled, the toggle
 * is displayed grayed out with a paid lock icon.
 */
const SnippetScanningSettingsSection: VoidFunctionComponent<SnippetScanningSettingsSectionProps> = ({
  isSnippetScanningEnabled,
  onToggleSnippetScanning,
  isScanningEnabled,
  canEditPolicy,
}) => {
  const isSnippetDetectionEnabled = useIsFeatureEnabled(SNIPPET_DETECTION);
  const isRestricted = !isSnippetDetectionEnabled;
  const restrictedClass = isRestricted ? css.issueSectionDisabled : undefined;

  return (
    <PaidFeatureGate isRestricted={isRestricted} hidePill useReadOnly={false}>
      <hr />
      <LabeledToggle
        label={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <span className={restrictedClass}>
              Scan for Snippets
            </span>
            {isRestricted && <PaidPill />}
          </span>
        }
        isChecked={isSnippetScanningEnabled}
        onChange={(isChecked) => onToggleSnippetScanning(isChecked)}
        isDisabled={!canEditPolicy || !isScanningEnabled || isRestricted}
      />
      <div className={restrictedClass}>
        <Section.Paragraph>
          This will turn on issue creation for Snippets.
        </Section.Paragraph>
      </div>
    </PaidFeatureGate>
  );
};

export default SnippetScanningSettingsSection;
